import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/form/button';
import { SearchBar } from '../../components/search-bar/search-bar';
import { Typography } from '../../components/typography/typography';
import useRequestQueryParams from '../../hooks/use-request-query-params';
import { DataTable, DataTableColumn, DataTableRow } from '../../components/table/data-table';
import { useRepresentativeApi } from '../../clients/quipli-service/representatives/representative-api';
import { useContext, useState } from 'react';
import { RepresentativeRole, SearchRolesRequest } from '../../clients/quipli-service/representatives/representative';
import { QuipliLink } from '../../components/link/quipli-link';
import { TenantContext } from '../../context/tenant-context';
import { PaginationData } from '../../clients/quipli-service/pagination/pagination';

export const RepresentativeList = () => {
  const { contextPath } = useContext(TenantContext);
  const { searchRoles } = useRepresentativeApi();
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<DataTableRow[]>([]);
  const [pagination, setPagination] = useState<PaginationData>();
  const columns: DataTableColumn[] = [
    {
      label: 'Role',
      key: 'role',
    },
    {
      label: 'Number of Templates',
      key: 'numberOfTemplates',
    },
    {
      label: 'Created At',
      key: 'createdAt',
    },
  ];

  const [params] = useSearchParams();
  const { request, setRequest } = useRequestQueryParams<SearchRolesRequest>(
    {
      keyword: params.get('keyword') ?? '',
      pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 10,
      pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    },
    () => fetchRoles(),
  );

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const { items, pagination } = await searchRoles({
        keyword: request.keyword,
        includeTemplates: true,
        pageSize: request.pageSize,
        pageNumber: request.pageNumber,
      });
      setData(items.map(buildDataRow));
      setPagination(pagination);
    } catch (e: any) {
      // toast(e.message.msg ?? e.message ?? 'Failed to fetch roles', 'error');
    } finally {
      setLoading(false);
    }
  };

  const buildDataRow = (r: RepresentativeRole): DataTableRow => {
    return {
      mobileHeader: (
        <>
          <div>
            <Typography type="text-sm" className="text-quipli-black-800">
              <QuipliLink
                to={`representatives/roles/${r.id}`}
                className="underline underline-offset-1 text-[var(--quipli-black-800)]"
              >
                {r.name}
              </QuipliLink>
            </Typography>
          </div>
        </>
      ),
      fields: {
        role: (
          <Typography type="text-sm" weight="regular">
            <QuipliLink
              to={`representatives/roles/${r.id}`}
              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            >
              {r.name}
            </QuipliLink>
          </Typography>
        ),
        numberOfTemplates: (
          <Typography type="text-sm" className="truncate text-quipli-black-800">
            {r.emailTemplates.length}
          </Typography>
        ),
        createdAt: (
          <div className="flex flex-col">
            <Typography type="text-sm" className="text-quipli-black-800">
              {r.createdAt.toFormat('DD')}
            </Typography>
            <Typography type="text-sm" className="text-grey-500">
              {r.createdAt.toFormat('t')}
            </Typography>
          </div>
        ),
      },
      className: 'cursor-pointer',
      redirectTo: `representatives/roles/${r.id}`,
    };
  };

  return (
    <div className="px-[10px] pb-[5px]">
      <Typography type="display-sm" weight="medium">
        Representative Roles
      </Typography>

      <div className="pt-5 flex items-center justify-between">
        <SearchBar placeholder="Search roles" defaultValue={request.keyword} setRequest={setRequest} />

        <div className="order-1 md:order2 flex gap-2">
          <Button
            className="button-yellow !border-0 h-[46px]"
            data-cy="create-order"
            onClick={() => navigate(`${contextPath}manager/representatives/roles/create`)}
          >
            <Typography
              type="text-md"
              weight="semi-bold"
              className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
            >
              Add New Role
            </Typography>
          </Button>
        </div>
      </div>

      <div className="pt-5">
        <DataTable
          columnNames={columns}
          data={data}
          pagination={pagination}
          request={request}
          setRequest={setRequest}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};
